import type { FlaggedAreaInput, FlaggedAreaSafety, ZoneGeoJsonPolygon } from '@chukua/common';
import { Form } from 'antd';
import { useEffect, useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { ApiError } from '../../../../../shared/shared.api';
import { useZonesMapConfig } from '../../../public/use-zones-map-config';
import { useZoneDrawing } from '../../editor/hooks/use-zone-drawing';
import { createFlaggedArea, getFlaggedArea, updateFlaggedArea, type FlaggedArea } from '../flagged-areas.api';
import { useFlaggedAreaMap } from './use-flagged-area-map';

export interface FlaggedAreaFormValues {
  name: string;
  safety: FlaggedAreaSafety;
  note?: string;
}

interface EditorArgs {
  /** An area id, or `new`. */
  areaId: string;
  onSaved: () => Promise<void>;
  onClose: () => void;
}

type LoadState = { status: 'loading' } | { status: 'ready'; area: FlaggedArea | null } | { status: 'error' };

/** Saves the area; returns the message to show inline when the server refuses, or null once saved. */
async function submitArea(area: FlaggedArea | null, input: FlaggedAreaInput): Promise<string | null> {
  try {
    await (area ? updateFlaggedArea(area.id, input) : createFlaggedArea(input));
    return null;
  } catch (err) {
    return err instanceof ApiError ? err.message : "Couldn't save the flagged area";
  }
}

/** Loads the area (none for `new`), draws its polygon on a map, and saves the form with the polygon. */
export function useFlaggedAreaEditor({ areaId, onSaved, onClose }: EditorArgs) {
  const [form] = Form.useForm<FlaggedAreaFormValues>();
  const toast = useToast();
  const mapConfig = useZonesMapConfig();
  const { containerRef, map } = useFlaggedAreaMap(mapConfig?.tileKey ?? null);
  const [load, setLoad] = useState<LoadState>(areaId === 'new' ? { status: 'ready', area: null } : { status: 'loading' });
  const [polygon, setPolygon] = useState<ZoneGeoJsonPolygon | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const area = load.status === 'ready' ? load.area : null;

  useEffect(() => {
    if (areaId === 'new') return;
    getFlaggedArea(areaId)
      .then((found) => {
        setLoad({ status: 'ready', area: found });
        setPolygon(found.polygon);
        form.setFieldsValue({ name: found.name, safety: found.safety, note: found.note ?? undefined });
      })
      .catch(() => setLoad({ status: 'error' }));
  }, [areaId, form]);

  const drawing = useZoneDrawing({
    map: load.status === 'ready' ? map : null,
    drawingKey: areaId,
    initialValue: () => ({ polygon: area?.polygon ?? null, centroid: null }),
    onChange: (geometry) => setPolygon(geometry.polygon),
  });

  async function save(values: FlaggedAreaFormValues): Promise<void> {
    if (!polygon) {
      setProblem('Draw the area on the map first.');
      return;
    }
    const input: FlaggedAreaInput = { name: values.name, safety: values.safety, note: values.note?.trim() || null, polygon };
    setIsSaving(true);
    const failure = await submitArea(area, input);
    setIsSaving(false);
    if (failure) {
      setProblem(failure);
      return;
    }
    await onSaved();
    toast.success(`Flagged area ${input.name} ${area ? 'updated' : 'created'}`);
    onClose();
  }

  function clearPolygon() {
    drawing.clearShape();
    setProblem(null);
  }

  const tileKey = mapConfig?.tileKey ?? null;
  return { form, load, tileKey, isMapConfigLoading: mapConfig === undefined, containerRef, drawing, polygon, problem, isSaving, save, clearPolygon };
}
